import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetPricingProfileQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { pricingProfileSelector } from "@/redux/slices/trip/selectors";
import { PricingProfile } from "@/redux/slices/trip/types";

interface PricingProfileState {
  isFetching: boolean;
  isRestricted: boolean;
  pricingProfile?: PricingProfile;
}

const PricingProfileContext = createContext<PricingProfileState | null>(null);

export const PricingProfileContextProvider = ({ children }: React.PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const profileName = searchParams.get("profileName");
  const { id = "" } = useParams();

  const { isFetching, error } = useGetPricingProfileQuery(id, { refetchOnMountOrArgChange: true });
  const pricingProfile = useAppSelector(pricingProfileSelector);
  const [isRestricted, setIsRestricted] = useState(false);

  // breadcrumbs setup (no extra history entries)
  useEffect(() => {
    if (!pricingProfile) return;

    const nextName = pricingProfile.name;
    if (profileName !== nextName) {
      const next = new URLSearchParams(searchParams);
      next.set("profileName", nextName);
      setSearchParams(next, { replace: true });
    }
    // Only depend on the values we actually compare/use
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingProfile?.name, profileName, setSearchParams]);

  // restricted check
  useEffect(() => {
    if (error || (!isFetching && !pricingProfile)) setIsRestricted(true);
  }, [error, isFetching, pricingProfile]);

  return <PricingProfileContext.Provider value={{ isFetching, isRestricted, pricingProfile }}>{children}</PricingProfileContext.Provider>;
};

export const usePricingProfileContext = () => {
  const context = useContext(PricingProfileContext);
  if (!context) throw new Error("usePricingProfileContext must be used within PricingProfileProvider");

  return context;
};
